// KpiCard — card editorial con etiqueta caps, valor grande y trend opcional.
//
// Layout vertical:
//   <Label uppercase tracking>    → 10px, color fg-tertiary
//   <Value 32px sans medium>      → 32px Inter, weight 500, ls -1.2px, tabular-nums
//   <Trend or Sub>                → 11.5px con color por tone
//
// Refactor 2026-05-26 (validado contra HTML diseño piloto UPG):
// la tipografía del valor pasó de Instrument Serif 42px a Inter sans 32px.
// La serif al tamaño grande se veía más "editorial" pero el HTML usa sans
// porque respira mejor en KPIs densos (lectura tipo cockpit, no editorial).

import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { Icon, type IconName } from './icon';
import { InfoHint } from './info-hint';
import { Sparkline, type SparklinePunto } from './sparkline';

export interface KpiCardProps {
  label: string;
  /** Explicación llana de la métrica (ⓘ al lado del label). */
  hint?: ReactNode;
  value: ReactNode;
  /** Sufijo al lado del valor (ej. "%", "u"). 13px no-serif. */
  valueSuffix?: ReactNode;
  /** Trend con dirección + texto + tono. */
  trend?: {
    direction: 'up' | 'down' | 'flat';
    text: ReactNode;
    tone: 'ok' | 'warn' | 'crit' | 'neutral';
  };
  /** Sub-label sin trend (texto secundario). */
  sub?: ReactNode;
  /** Tono del sub (afecta color). */
  subTone?: 'crit' | 'warn' | 'ok' | 'neutral';
  /** Si true, agrega un borde izquierdo separador (estilo D1 mockup). */
  divider?: boolean;
  /** Sparkline 7 puntos opcional al lado derecho del valor (estilo C1 redesign). */
  sparkline?: {
    puntos: SparklinePunto[];
    tone?: 'ok' | 'warn' | 'crit' | 'neutral';
  };
  /**
   * Valor en una sola línea y con tamaño fluido (28 px a 1280 de ancho, 32 px
   * desde ~1450): para filas de KPIs que se proyectan ("5h 39m", "$ 21.202.415"
   * se partían en dos renglones). Sin la prop, el valor queda como siempre.
   */
  fitValue?: boolean;
  className?: string;
}

const toneColors = {
  ok: 'text-ok',
  warn: 'text-warn',
  crit: 'text-crit',
  neutral: 'text-fg-tertiary',
} as const;

const trendIconName: Record<NonNullable<KpiCardProps['trend']>['direction'], IconName | null> = {
  up: 'arrow-up',
  down: 'arrow-down',
  flat: null,
};

export function KpiCard({
  label,
  hint,
  value,
  valueSuffix,
  trend,
  sub,
  subTone = 'neutral',
  divider = false,
  sparkline,
  fitValue = false,
  className,
}: KpiCardProps) {
  return (
    <div
      className={cn('flex flex-col gap-2 px-5', divider ? 'border-l border-border' : '', className)}
    >
      <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
        {label}
        {hint ? <InfoHint label={label}>{hint}</InfoHint> : null}
      </span>
      <div className="flex items-end justify-between gap-2">
        <div className="flex items-baseline gap-1.5">
          <span
            // El tamaño va ANTES de leading-none: tailwind-merge descarta un
            // leading-* que preceda a una clase de tamaño de texto.
            className={cn(
              fitValue ? 'whitespace-nowrap text-[clamp(1.5rem,2.2vw,2rem)]' : 'text-[32px]',
              'font-medium leading-none text-fg',
            )}
            style={{ letterSpacing: '-1.2px', fontVariantNumeric: 'tabular-nums' }}
          >
            {value}
          </span>
          {valueSuffix ? (
            <span className="text-[13px] tracking-tight text-fg-secondary">{valueSuffix}</span>
          ) : null}
        </div>
        {sparkline ? (
          <Sparkline puntos={sparkline.puntos} tone={sparkline.tone ?? 'neutral'} />
        ) : null}
      </div>
      <div className="min-h-4 text-[11.5px]" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {trend ? (
          <span
            className={cn('inline-flex items-center gap-1 font-medium', toneColors[trend.tone])}
          >
            {trendIconName[trend.direction] ? (
              <Icon name={trendIconName[trend.direction]!} size={12} stroke={2} />
            ) : null}
            {trend.text}
          </span>
        ) : sub ? (
          <span className={toneColors[subTone]}>{sub}</span>
        ) : null}
      </div>
    </div>
  );
}
